import { useMemo, useState } from 'react'
import classNames from 'classnames'

import { CircleSuccessLineIcon } from '../../../icons/CircleSuccessLineIcon'
import { NavigationArrowDownIcon } from '../../../icons/NavigationArrowDownIcon'
import { NameDto } from '../../../model/NameDto'
import { Dropdown } from '../Dropdown/Dropdown'
import { DropdownCard } from '../Dropdown/DropdownCard'
import { DropdownList } from '../Dropdown/DropdownList'
import { DropdownListItem } from '../Dropdown/DropdownListItem'
import { Input } from '../Input/Input'

import styles from './DropdownInput.module.scss'

export interface DropdownInputProps<T extends NameDto<string | number>> {
  value?: T['id']
  onChange: (option: T) => void
  options?: T[]
  variant?: 'default' | 'compact'
  placeholder?: string
  hint?: string
  error?: boolean
  isScrollable?: boolean
  isFullWidth?: boolean
}

export const DropdownInput = <T extends NameDto<string | number>>(
  props: DropdownInputProps<T>
): JSX.Element => {
  const {
    value,
    onChange,
    options,
    placeholder,
    variant = 'default',
    hint,
    error,
    isScrollable,
    isFullWidth = false,
  } = props

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const selectedName = useMemo(() => {
    return options?.find((option) => option.id === value)?.name
  }, [value, options])

  const handleSelect = (option: T) => {
    onChange?.(option)
    setIsDropdownOpen(false)
  }

  return (
    <div
      className={classNames({
        [styles.fullWidth]: isFullWidth,
      })}
    >
      <Dropdown
        direction='bottom'
        isOpen={isDropdownOpen}
        setIsOpen={setIsDropdownOpen}
        content={
          <DropdownCard>
            <DropdownList variant={variant} isScrollable={isScrollable}>
              {options?.map((option) => {
                const isActive = option.id === value

                return (
                  <DropdownListItem
                    variant={variant}
                    key={option.id}
                    onClick={() => handleSelect(option)}
                    isActive={isActive}
                  >
                    {option.name}
                  </DropdownListItem>
                )
              })}
            </DropdownList>
          </DropdownCard>
        }
      >
        <Input
          value={selectedName || ''}
          onWrapperClick={() => setIsDropdownOpen(true)}
          placeholder={placeholder}
          endIcon={NavigationArrowDownIcon}
          hint={hint}
          error={error}
          readOnly
        />
      </Dropdown>
    </div>
  )
}
